---
title: MCP
description: Connect your AI agent to audio/ui with the MCP server, to list components, read their docs and search the catalog.
---

MCP is an open protocol that lets AI agents use tools. The audio/ui MCP server gives your agent access to every component and its current docs.

The server is remote, needs no account and no install:

```
https://www.audio-ui.dev/mcp
```

## Connect your client

<Tabs defaultValue="claude">

<TabsList>
  <TabsTrigger value="claude"><Icons.claude /> Claude Code</TabsTrigger>
  <TabsTrigger value="cursor"><Icons.cursor /> Cursor</TabsTrigger>
  <TabsTrigger value="vscode"><Icons.vscode /> VS Code</TabsTrigger>
  <TabsTrigger value="other">Other</TabsTrigger>
</TabsList>

<TabsContent value="claude">

```bash
claude mcp add --transport http audio-ui https://www.audio-ui.dev/mcp
```

</TabsContent>

<TabsContent value="cursor">

Add this to `.cursor/mcp.json`:

```json
{
  "mcpServers": {
    "audio-ui": {
      "url": "https://www.audio-ui.dev/mcp"
    }
  }
}
```

</TabsContent>

<TabsContent value="vscode">

Add this to `.vscode/mcp.json`:

```json
{
  "servers": {
    "audio-ui": {
      "type": "http",
      "url": "https://www.audio-ui.dev/mcp"
    }
  }
}
```

</TabsContent>

<TabsContent value="other">

Any client that supports remote servers over Streamable HTTP works. Add a server of type `http` with the URL above.

</TabsContent>

</Tabs>

## Tools

<TypeTable title="Tools">
<TypeRow name="search_docs">Search the docs text in plain words. Each hit names a component and a section to read next.</TypeRow>
<TypeRow name="list_components">All components with docs URL and install command, or one `kind`: elements, workstation or devices.</TypeRow>
<TypeRow name="get_component_docs">One component's docs as markdown, or one section of them with `section`. A reply over 25,000 characters is cut, and says which sections to ask for.</TypeRow>
<TypeRow name="search_catalog">Find ready-made blocks by words like `synth`, 25 per page. Pass `next_offset` as `offset` for the next page.</TypeRow>
</TypeTable>

## Usage

Ask your agent in plain words:

- Build a synth control panel with Knob, XY Pad and Fader.
- Add an equalizer with a Plot and draggable band handles.
- Make a piano roll with a keyboard, a velocity lane and a loop range.

## With shadcn

Because audio/ui is also a shadcn registry, the shadcn MCP server can install components too. Run `npx shadcn@latest mcp init` and add the `@audio-ui` registry from the [Registry guide](/docs/registry).

For the best results, also install the [agent skill](/docs/agents).
